import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatFileSize } from "../lib/jobForm";
import { attachmentSrc, formatAttachmentDate } from "../lib/attachmentKind";
import type { AttachmentDraft } from "../types";
import styles from "./AttachmentGallery.module.css";

export interface AttachmentGalleryProps {
  /** Viewable images only, in the order they appear in the grid. */
  images: readonly AttachmentDraft[];
  /** Index of the image to open on. */
  startIndex: number;
  /** Persisted job id — supplies the download URL for stored images. */
  jobId?: string;
  onClose: () => void;
}

/**
 * Full-screen image carousel for a job's photo attachments.
 *
 * Deliberately not built on {@link Modal}: that shell is a titled document
 * panel on a light scrim, and a photo has to be judged against a dark, quiet
 * backdrop with the chrome kept out of the way. What it does share is the
 * behaviour that makes an overlay usable — Escape closes, the backdrop closes,
 * focus is trapped and returned, and the page behind does not scroll.
 *
 * Navigation wraps in both directions, so a two-photo job does not dead-end on
 * either arrow. Rendered in a portal because the form that owns it sits inside
 * a modal panel with its own stacking context and overflow clipping.
 */
export function AttachmentGallery({ images, startIndex, jobId, onClose }: AttachmentGalleryProps) {
  const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), Math.max(images.length - 1, 0)));
  const panelRef = useRef<HTMLDivElement>(null);
  const count = images.length;

  const go = useCallback(
    (delta: number): void => {
      setIndex((current) => (count === 0 ? 0 : (current + delta + count) % count));
    },
    [count],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        // The viewer opens on top of the job modal; without this the same
        // Escape would close both.
        event.stopPropagation();
        onClose();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        go(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        go(-1);
      }
    };
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Capture phase: the job modal listens on document too, and the viewer is
    // the innermost layer, so it must see the key first.
    document.addEventListener("keydown", handleKeyDown, true);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [go, onClose]);

  // A deleted last image (or an empty list handed in) closes rather than
  // rendering an empty black screen.
  useEffect(() => {
    if (count === 0) onClose();
    else if (index > count - 1) setIndex(count - 1);
  }, [count, index, onClose]);

  if (count === 0) return null;
  const image = images[Math.min(index, count - 1)];
  const source = attachmentSrc(image, jobId);

  return createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${index + 1} of ${count}: ${image.name}`}
      ref={panelRef}
      tabIndex={-1}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className={styles.bar}>
        <div className={styles.heading}>
          <p className={styles.name} title={image.name}>
            {image.name}
          </p>
          <p className={styles.meta}>
            {index + 1} of {count} · {formatFileSize(image.size)}
            {image.uploadedAt ? ` · ${formatAttachmentDate(image.uploadedAt)}` : " · Not uploaded yet"}
          </p>
        </div>
        <div className={styles.barActions}>
          {image.persisted && jobId && (
            <a
              className={styles.action}
              href={`/api/jobs/${jobId}/attachments/${image.id}`}
              download={image.name}
            >
              Download
            </a>
          )}
          <button type="button" className={styles.action} onClick={onClose}>
            Close
          </button>
        </div>
      </header>

      <div className={styles.stage}>
        {count > 1 && (
          <button
            type="button"
            className={`${styles.nav} ${styles.navPrev}`}
            aria-label="Previous image"
            onClick={() => go(-1)}
          >
            <Chevron direction="left" />
          </button>
        )}
        {source ? (
          <img className={styles.image} src={source} alt={image.name} />
        ) : (
          <p className={styles.missing}>This image has no stored file yet.</p>
        )}
        {count > 1 && (
          <button
            type="button"
            className={`${styles.nav} ${styles.navNext}`}
            aria-label="Next image"
            onClick={() => go(1)}
          >
            <Chevron direction="right" />
          </button>
        )}
      </div>

      {count > 1 && (
        <div className={styles.filmstrip}>
          {images.map((candidate, candidateIndex) => {
            const thumbSource = attachmentSrc(candidate, jobId);
            return (
              <button
                key={candidate.id}
                type="button"
                className={candidateIndex === index ? styles.filmActive : styles.film}
                aria-label={`Show ${candidate.name}`}
                aria-current={candidateIndex === index}
                onClick={() => setIndex(candidateIndex)}
              >
                {thumbSource && <img className={styles.filmImage} src={thumbSource} alt="" />}
              </button>
            );
          })}
        </div>
      )}
    </div>,
    document.body,
  );
}

/** Carousel arrow glyph. Stroked, so it inherits the button's colour. */
function Chevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <path
        d={direction === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
